import {useState} from "react";
import {Link} from "react-router-dom";
import usePathname from "@/hooks/use-pathname.tsx";
import {
    NavigationMenu,
    NavigationMenuItem,
    NavigationMenuList,
} from "@/components/ui/navigation-menu.tsx";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet.tsx";

import {EnterIcon} from "@radix-ui/react-icons";
import {buttonVariants} from "../ui/button.tsx";
import {Menu} from "lucide-react";
import {ModeToggle} from "../ui/mode-toggle-c.tsx";
import {LogoIcon} from "../Icons.tsx";
import ProfileDropdown from "@/components/header/ProfileDropdown.tsx";
import {useAuth} from "@/contexts/AuthContext.tsx";
import {useTranslation} from "react-i18next";
import LocaleToggle from "@/components/locale-toggle.tsx";
import {useRandomBackgroundColor} from "@/contexts/RandomBgColorContext.tsx";
import {stopImpersonation} from "@/services/impersonationService.ts";
import {useQuery} from "@tanstack/react-query";
import {fetchLastDateToSelectMentor} from "@/services/settingsService.ts";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {getMenteeById} from "@/services/menteeService.ts";

function isRouteActive(pathname: string, href: string): boolean {
    if (href.startsWith("#")) return false;
    if (href === "/dashboard") return pathname === "/dashboard";
    return pathname === href || pathname.startsWith(href + "/");
}

interface RouteProps {
    href: string;
    label: string;
}

const guestRouteList: RouteProps[] = [
    {
        href: "#about",
        label: "about",
    },
    {
        href: "#howItWorks",
        label: "howItWorks",
    },
    {
        href: "#faq",
        label: "faq",
    },
];

const adminRouteList: RouteProps[] = [
    {
        href: "/dashboard",
        label: "dashboard",
    },
    {
        href: "/mentors",
        label: "mentors",
    },
    {
        href: "/mentees",
        label: "mentees",
    },
    {
        href: "/suggestions",
        label: "suggestions",
    },
    {
        href: "/users",
        label: "manageUsers",
    },
    {
        href: "/match-criteria",
        label: "manageCriteria",
    },
];

const getMentorRouteList = (mentorId: string) => [
    {
        href: "/dashboard",
        label: "dashboard",
    },
    {
        href: `/mentors/${mentorId}/mentees`,
        label: "myGroup",
    }
];

const getMenteeRouteList = (
    menteeId: string,
    selectMentorDeadline: Date | undefined,
    mentorId: number | undefined
) => {
    const today = new Date();
    const deadlinePassed = selectMentorDeadline && today > selectMentorDeadline;
    const hasMentor = mentorId != null;
    
    const routes = [
        {
            href: "/dashboard",
            label: "dashboard",
        },
    ];
    
    // Show "My Group" if deadline has passed and mentee has a mentor
    // Otherwise show "Find a Mentor"
    if (deadlinePassed && hasMentor) {
        routes.push({
            href: `/mentors/${mentorId}/mentees`,
            label: "myGroup",
        });
    } else {
        routes.push({
            href: `/mentees/${menteeId}/preference`,
            label: "findMentor",
        });
    }
    
    return routes;
};

export const Header = () => {
    const pathname = usePathname();
    const {loggedInUser: user} = useAuth();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    
    // Fetch mentor selection deadline for mentees
    const {data: selectMentorDeadline} = useQuery({
        queryKey: queryKeys.fetchSettings.lastDateToSelectMentor,
        queryFn: fetchLastDateToSelectMentor,
        enabled: user?.role === "mentee",
    });
    
    // Fetch mentee data to get mentorId
    const {data: menteeData} = useQuery({
        queryKey: [...queryKeys.fetchMentees.one, user?.mentee?.id],
        queryFn: () => getMenteeById(user?.mentee?.id ?? 0),
        enabled: user?.role === "mentee" && user?.mentee?.id != null,
    });
    
    const routeList = user ?
        user.role === "admin" ? adminRouteList :
            user.role === "mentor" ? getMentorRouteList(String(user.mentor?.id ?? "")) : 
                getMenteeRouteList(
                    String(user.mentee?.id ?? ""),
                    selectMentorDeadline,
                    menteeData?.mentor?.id
                ) :
        guestRouteList;

    const {t} = useTranslation();
    const {lightBg, darkBg} = useRandomBackgroundColor();

    const headerBgClass = user
        ? `${lightBg} ${darkBg === "bg-charcoal" ? "dark:bg-charcoal" : "dark:bg-slate"} text-charcoal dark:text-white`
        : "bg-transparent";

    return (
        <header className={`sticky top-0 z-40 w-full ${headerBgClass}`}>
            {user?.impersonator && (
                <div className="w-full bg-arcticBlue text-charcoal">
                    <div className="container flex flex-wrap items-center justify-between gap-2 py-2 text-sm font-body">
                        <span>
                            {t("navbar.impersonatingAs")}{" "}
                            <strong className="font-heading">{user.fullName}</strong>{" "}
                            ({t(`common.${user.role}`)})
                        </span>
                        <span className="text-xs text-slate dark:text-ash">
                            {t("navbar.impersonationReadOnly")}
                        </span>
                        <button
                            className="underline underline-offset-2"
                            onClick={async () => {
                                await stopImpersonation();
                                window.location.href = "/dashboard";
                            }}
                        >
                            {t("navbar.stopImpersonation")}
                        </button>
                    </div>
                </div>
            )}
            <NavigationMenu className="mx-auto">
                <NavigationMenuList className="container h-16 px-4 w-screen flex justify-between">
                    <div className="flex">
                        <NavigationMenuItem className="font-bold font-heading flex">
                            <a
                                href="/"
                                className="ml-2 font-bold font-heading text-xl flex"
                            >
                                <span className="sr-only">DIVERT Mental Health: Mentor Matcher</span>
                                <LogoIcon/>
                            </a>
                        </NavigationMenuItem>

                        {/* desktop */}
                        <nav className="hidden md:flex gap-2 ml-6">
                            {routeList.map((route: RouteProps, i) => {
                                const active = isRouteActive(pathname, route.href);
                                const navClass = `text-[17px] font-heading uppercase ${buttonVariants({
                                    variant: "ghost",
                                })} ${active ? "border-b-2 border-accent rounded-none" : ""}`;
                                if (route.href.startsWith("#")) {
                                    return (
                                        <a
                                            href={route.href}
                                            key={i}
                                            className={navClass}
                                        >
                                            {t(`navbar.${route.label}`)}
                                        </a>
                                    );
                                }
                                return (
                                    <Link
                                        to={route.href}
                                        key={i}
                                        className={navClass}
                                    >
                                        {t(`navbar.${route.label}`)}
                                    </Link>
                                );
                            })}
                        </nav>
                    </div>

                    {/* mobile */}
                    <span className="flex md:hidden gap-2">
                        <ModeToggle/>
                        <LocaleToggle/>
                        {user && <ProfileDropdown user={user}/>}

                        <Sheet open={isOpen} onOpenChange={setIsOpen}>
                            <SheetTrigger className="px-2">
                                <Menu
                                    className="flex md:hidden h-5 w-5"
                                    onClick={() => setIsOpen(true)}
                                >
                                    <span className="sr-only">Menu Icon</span>
                                </Menu>
                            </SheetTrigger>

                            <SheetContent side={"left"}>
                                <SheetHeader>
                                    <SheetTitle className="font-bold text-xl">
                                        {t("common.appTitle")}
                                    </SheetTitle>
                                </SheetHeader>
                                <nav className="flex flex-col justify-center items-center gap-2 mt-4">
                                    {routeList.map(({href, label}: RouteProps) => {
                                        const active = isRouteActive(pathname, href);
                                        const navClass = `${buttonVariants({variant: "ghost"})} ${active ? "border-b-2 border-accent rounded-none" : ""}`;
                                        if (href.startsWith("#")) {
                                            return (
                                                <a
                                                    key={label}
                                                    href={href}
                                                    onClick={() => setIsOpen(false)}
                                                    className={navClass}
                                                >
                                                    {t(`navbar.${label}`)}
                                                </a>
                                            );
                                        }
                                        return (
                                            <Link
                                                key={label}
                                                to={href}
                                                onClick={() => setIsOpen(false)}
                                                className={navClass}
                                            >
                                                {t(`navbar.${label}`)}
                                            </Link>
                                        );
                                    })}
                                    {!user &&
                                        <a
                                            href="/auth/ibmid"
                                            className={`w-[110px] border ${buttonVariants({
                                                variant: "secondary",
                                            })}`}
                                        >
                                            {t('common.signIn')}
                                            <EnterIcon className="ml-2 w-5 h-5"/>
                                        </a>
                                    }
                                </nav>
                            </SheetContent>
                        </Sheet>
                    </span>

                    <div className="hidden md:flex gap-3">
                        <ModeToggle/>
                        <LocaleToggle/>
                        {user ? (
                            <ProfileDropdown user={user}/>
                        ) : (
                            <a
                                href="/auth/ibmid"
                                className={`border ${buttonVariants({variant: "default"})}`}
                            >
                                {t('common.signIn')}
                                <EnterIcon className="ml-2 w-5 h-5"/>
                            </a>
                        )}
                    </div>
                </NavigationMenuList>
            </NavigationMenu>
        </header>
    );
};
